package gr.tech.pdfgenerator.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.pdfgenerator.components.generation.PDFGenerationUI
import gr.tech.pdfgenerator.components.generation.t3DocumentFooter
import gr.tech.pdfgenerator.components.generation.t3DocumentHeader
import gr.tech.pdfgenerator.components.generation.t3FeeTable
import gr.tech.pdfgenerator.components.generation.t3QEESMTable
import gr.tech.pdfgenerator.components.generation.t3SignBelowBoxes
import gr.tech.pdfgenerator.components.generation.t3SignatureRow
import gr.tech.pdfgenerator.components.layouts.AuthPageLayout
import gr.tech.pdfgenerator.components.layouts.PDFLayout
import gr.tech.pdfgenerator.infrastructure.api.savePDFWithProjectID
import gr.tech.pdfgenerator.infrastructure.data.GeneratorData
import gr.tech.pdfgenerator.models.user.USER_ROLES
import gr.tech.pdfgenerator.styles.*
import gr.tech.pdfgenerator.infrastructure.pdf.GeneratePDFTool
import gr.tech.pdfgenerator.infrastructure.pdf.PDFGenStatus
import gr.tech.pdfgenerator.util.RES
import gr.tech.pdfgenerator.util.BACKEND
import gr.tech.pdfgenerator.util.CONSTANTS
import gr.tech.pdfgenerator.util.formatCurrency
import gr.tech.pdfgenerator.util.roundToOneDecimalPlace
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.math.roundToLong

@Page(routeOverride = "tierthree")
@Composable
fun tierThreeGeneratePage() {
    AuthPageLayout(
        title = "Inputs",
        requiredRoles = listOf(USER_ROLES.CONFIRMED_USER.role)
    ) {
        tierThreeGenerateScreen()
    }
}

@Composable
fun tierThreeGenerateScreen() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    val generator = GeneratePDFTool()
    var generationStatus: PDFGenStatus by remember { mutableStateOf(PDFGenStatus.GENERATING) }

    val projectTitleFormatted = GeneratorData.generalInputsState.value.projectName.replace(" ", "")
    val documentTitle = "${projectTitleFormatted}-${getCurrentDateString()}"

    /*
        Want to convert each generation page for more variables top level,
        so that it is easier to manipulate, change, find. Below should be strictly UI.
     */
    var t3Data by GeneratorData.tierThreeState
    var lightPropData by GeneratorData.propCalculationsState
    val generalData by GeneratorData.generalInputsState

    // Summary Data
    val t3Rebate = formatCurrency(if (t3Data.proposalOverwriteCheck) t3Data.rebatesOverwrite else lightPropData.rebates)
    val t3NetProjectCost = formatCurrency(if (t3Data.proposalOverwriteCheck) t3Data.projectCostOverwrite else lightPropData.projectCost)
    val t3AnnualSavings = formatCurrency(if (t3Data.proposalOverwriteCheck) t3Data.totalAnnualSavingsOverwrite else lightPropData.totalAnnualSavings)
    val t3TotalSavings = formatCurrency(if (t3Data.proposalOverwriteCheck) t3Data.cummCashFlowListOverwrite else lightPropData.cummCashFlowList.last())
    val t3AnnualizedROI = if (t3Data.proposalOverwriteCheck) {
        if (t3Data.annualizedROIOverwrite > 0) "${(t3Data.annualizedROIOverwrite * 100).roundToLong()}%" else "0%"
    } else {
        if (lightPropData.annualizedROI > 0) "${(lightPropData.annualizedROI * 100).roundToLong()}%" else "0%"
    }
    val t3SimplePayback = if (t3Data.proposalOverwriteCheck) {
        if (t3Data.simplePaybackOverwrite > 0) "${t3Data.simplePaybackOverwrite.roundToOneDecimalPlace()} Years" else "0 Years"
    } else {
        if (lightPropData.simplePayback > 0) "${lightPropData.simplePayback.roundToOneDecimalPlace()} Years" else "0 Years"
    }

    val t3SignDate = generalData.effectiveDate
    val t3CompanyName = generalData.companyName
    val t3UtilityCompany = generalData.companyUtility
    val t3State = if (t3Data.addressOverwriteCheck) t3Data.stateOverwrite else generalData.companyState
    val t3CompanyAddress = if (t3Data.addressOverwriteCheck) t3Data.addressOverwrite else generalData.companyAddress
    val t3City = if (t3Data.addressOverwriteCheck) t3Data.cityOverwrite else generalData.companyCity
    val t3Zip = if (t3Data.addressOverwriteCheck) t3Data.zipOverwrite else generalData.companyZip

    val t3QEESMList = t3Data.equipmentList

    val repGreenRebates = t3Data.grRepName
    val repTitle = t3Data.grRepTitle

    LaunchedEffect(Unit) {
        if (GeneratorData.project.value.id != null) {
            generator.genPDF(
                onGenerationStatus = { status ->
                    console.log("genPDF Status: $status")
                    generationStatus = status
                }
            )
        } else {
            ctx.router.tryRoutingTo(BACKEND.ROUTES.PROJECTS)
        }
    }

    /*
        -------------- PDF UI --------------

        Everything below here is the UI and the generated PDF page.
        Please no fancy calculations or logic down here, try to keep it UI only.

        Some rules I tried to follow:
        - Margin, Padding, one-off styles were allowed in-line.
        - For styles that require more than one property, turn it into a style.
        - Keep Columns, Rows, and function headers on a one line, so that collapsing for human readability/debugging is nicer.

        There are a lot of lines below, so trying to keep things to a minimum in terms of length for this section.

        ---------------------------------------
     */

    PDFLayout(documentTitle) {

        PDFGenerationUI(
            generator = generator,
            status = generationStatus,
            onGoBack = {
                ctx.router.navigateTo("/inputs?projectid=${GeneratorData.project.value.id}")
            },
            onStatusUpdate = { generationStatus = it },
            onSave = { blob ->
                scope.launch {
                    if (GeneratorData.project.value.id != null) {
                        savePDFWithProjectID(
                            projectId = GeneratorData.project.value.id!!,
                            projectName = documentTitle,
                            projectType = CONSTANTS.PROJECT_TYPES.TIER_THREE,
                            pdfBlob = blob
                        )
                    }
                    generationStatus = PDFGenStatus.DOWNLOADED
                }
            }
        )

        // Page to Print
        Div(ElementToPrintStyle.toAttrs {
            id("element-to-print")
        }) {

            // Page 1
            Div(pageStyle.toModifier().toAttrs()) {

                Column(Modifier.margin(top = 54.px).pageStyleLeftRightPadding().fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                    t3DocumentHeader()

                    Img(
                        src = RES.GENERAL.grlogocenter, alt = "",
                        attrs = GeneralLogoSmallStyle.toModifier().margin(top = 48.px).toAttrs()
                    )

                    if (t3Data.proposalShowInPDF) {
                        H1(t3DocumentSectionFontStyle.toModifier().margin(top = 16.px).toAttrs()) {
                            Text("Rebate Estimate Summary")
                        }
                    }
                }

                // Summary Box
                if (t3Data.proposalShowInPDF) {
                    Row(t3SummaryRowContainerStyle.toModifier()) {

                        Column(summaryColumnStyle.toModifier(), horizontalAlignment = Alignment.Start) {
                            pdfSummaryBox("Rebate", t3Rebate)
                            pdfSummaryBox("Net Project Cost", t3NetProjectCost)
                        }
                        Column(summaryColumnStyle.toModifier(), horizontalAlignment = Alignment.Start) {
                            pdfSummaryBox("Annual Savings", t3AnnualSavings)
                            pdfSummaryBox("Total Savings (10 yrs)", t3TotalSavings)
                        }
                        Column(summaryColumnStyle.toModifier(), horizontalAlignment = Alignment.Start) {
                            pdfSummaryBox("Annualized ROI", value = t3AnnualizedROI)
                            pdfSummaryBox("Simple Payback", value = t3SimplePayback)
                        }
                    }
                }


                // Rebate Procurement Agreement
                Column(Modifier.fillMaxWidth().pageStyleLeftRightPadding(), horizontalAlignment = Alignment.CenterHorizontally,) {
                    H1(t3DocumentSectionFontStyle.toModifier().margin(top = 16.px).toAttrs()) {
                        Text("Rebate Procurement Agreement")
                    }

                    P(GeneralParagraphStyle.toModifier().margin(top = 24.px).toAttrs()) {
                        Text(
                            "We're thrilled to welcome you to Green Rebates. Our mission is to help you maximize available for energy-efficient" +
                                    " upgrades while managing the rebate process. This agreement outlines the framework of our collaboration on securing the best possible outcome for your project."
                        )
                    }

                    P(GeneralParagraphStyle.toModifier().margin(top = 12.px).toAttrs()) {
                        Text("To being, please acknowledge the following key details of the process, then review the agreement and sign below.")
                    }

                    Ul(t3SignBelowListStyle.toModifier().margin(leftRight = 16.px).toAttrs()) {
                        t3SignBelowBoxes() {
                            P(GeneralParagraphStyle.toModifier().then(t3SignBoxParagraphStyle.toModifier()).toAttrs()) {
                                Text("I acknowledge the purchase and installation of equipment should be closely coordinated with Green Rebates to ensure; the rebate submission has first been approved, and the proposed equipment, the timing of purchase, and installation meet rebate program eligibility requirements.")
                            }
                        }
                        t3SignBelowBoxes() {
                            P(GeneralParagraphStyle.toModifier().then(t3SignBoxParagraphStyle.toModifier()).toAttrs()) {
                                Text("I understand any changes to equipment or facility plans during the rebate submission process should be clearly communicated to Green Rebates to avoid risks to eligibility and rebate payment amount.")
                            }
                        }

                        t3SignBelowBoxes() {
                            P(GeneralParagraphStyle.toModifier().then(t3SignBoxParagraphStyle.toModifier()).toAttrs()) {
                                Text("I will designate Green Rebates as payee for the rebate in relation to the Equipment Measures listed below, and Green Rebates will remit payment to me less the percentage fee designated in the Fee Table ")
                                SpanText(" (“Exhibit A”).", modifier = Modifier.fontStyle(FontStyle.Italic))
                            }
                        }
                    }
                }

                // Fee Table and QEESM Table
                Row(Modifier.fillMaxWidth().pageStyleLeftRightPadding().margin(top = 48.px), horizontalArrangement = Arrangement.SpaceBetween) {
                    Column(
                        modifier = Modifier.width(200.px).margin(right = 32.px)
                    ) {
                        H3(t3DocumentTitleFontStyle.toModifier().fontSize(15.px).toAttrs()) {
                            Text("Fee Table (\"Exhibit A\")")
                        }

                        t3FeeTable(
                            lightingPercentage = t3Data.lightingPercentage,
                            dehumidifierPercentage = t3Data.dehumidifierPercentage,
                            heatCurtainsPercentage = t3Data.heatCurtainPercentage,
                            hvacPercentage = t3Data.hvacPercentage
                        )
                    }

                    Column(
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        H3(t3DocumentTitleFontStyle.toModifier().fontSize(15.px).toAttrs()) {
                            Text("Qualifying Electric Energy Saving Measures (\"Exhibit B\")")
                        }

                        t3QEESMTable(
                            equipmentList = t3QEESMList
                        )
                    }
                }

                Spacer()

                Column(Modifier.fillMaxWidth().margin(bottom = 54.px).pageStyleLeftRightPadding(), horizontalAlignment = Alignment.CenterHorizontally,) {
                    t3DocumentFooter(currPage = 1, maxPages = 3)
                }
            }

            // Page 2
            Div(pageStyle.toModifier().toAttrs()) {

                Column(
                    modifier = Modifier.margin(top = 54.px).pageStyleLeftRightPadding().fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    t3DocumentHeader()

                    // THIS REBATE PROCUREMENT...
                    P(t3TightParagraphStyle.toModifier().margin(top = 16.px).toAttrs()) {
                        Text("THIS REBATE PROCUREMENT AGREEMENT (the \"")
                        SpanText("Agreement", BoldItalic.toModifier())
                        Text("\") is entered into as of $t3SignDate ")
                        Text("(the \"")
                        SpanText("Effective Date", BoldItalic.toModifier())
                        Text("\"), by and between ")
                        SpanText("GREEN REBATES, L.L.C.", Modifier.textDecorationLine(TextDecorationLine.Underline))
                        Text(", a State of Delaware limited liability company with its principal place of business located at ${RES.GENERAL.FULL_ADDRESS} (hereinafter \"")
                        SpanText("Company", BoldItalic.toModifier())
                        Text("\"); and $t3CompanyName.")
                        Text(", a company of the State of $t3State ")
                        Text("with a principal place of business located at $t3CompanyAddress ")
                        Text("(\"")
                        SpanText("Customer", BoldItalic.toModifier())
                        Text("\")")
                        Text("(collectively hereinafter ")
                        SpanText("Company", BoldItalic.toModifier())
                        Text(" and ")
                        SpanText("Customer", BoldItalic.toModifier())
                        Text(" referred to as the, \"")
                        SpanText("Parties", BoldItalic.toModifier())
                        Text("\").")
                    }

                    // NOW, THEREFORE, in consideration...
                    P(t3TightParagraphStyle.toModifier().margin(top = 8.px).toAttrs()) {
                        Text(
                            "NOW, THEREFORE, in consideration of the agreements, representations, warranties, " +
                                    "promises and covenants contained herein, and other good and valuable consideration, the receipt, " +
                                    "sufficiency and adequacy of which are hereby acknowledged, the "
                        )
                        SpanText("Parties", BoldItalic.toModifier())
                        Text(", intending to be legally bound, agree to the foregoing and as follows:")
                    }
                }

                Column(
                    modifier = Modifier.padding(leftRight = 24.px).fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    // List of paragraphs
                    Ol(t3TermsListStyle.toModifier().margin(top = 8.px).toAttrs()) {

                        // Scope of Work; General.
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Scope of Work; General.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("By signing this Agreement,")
                                SpanText(" Customer ", BoldItalic.toModifier())
                                Text("hereby authorizes")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("to procure on the")
                                SpanText(" Customer's ", BoldItalic.toModifier())
                                Text("behalf with the Customer's service provider $t3UtilityCompany")
                                Text(" (the \"")
                                SpanText("Service Provider", BoldItalic.toModifier())
                                Text("\"), or Affiliated Program, any rebates and incentives in relation to ")
                                SpanText(
                                    "Qualifying Electric Energy Saving Measures, ",
                                    Modifier.fontStyle(FontStyle.Italic)
                                )
                                Text("as outlined in the attached Exhibit B (the \"")
                                SpanText("Measures", BoldItalic.toModifier())
                                Text("\"). ")

                                Text("For the limited purpose herin, as authorized agent-in-fact,")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("will prepare and submit any, and all, necessary forms and documents to applicable federal, state, or local energy efficiency rebate or incentive programs, for approval.  Additionally,")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("will obtain written authorization from")
                                SpanText(" Customer", BoldItalic.toModifier())
                                Text(", where required, including any third-party release forms mandated by rebate programs.")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("will work with")
                                SpanText(" Customer ", BoldItalic.toModifier())
                                Text("to ensure the implementation of the ")
                                SpanText(" Measures ", BoldItalic.toModifier())
                                Text("required for rebate or incentive. It is the sole responsibility of the ")
                                SpanText(" Customer ", BoldItalic.toModifier())
                                Text("to install all ")
                                SpanText(" Measures ", BoldItalic.toModifier())
                                Text("in a timely manner. ")
                            }
                        }

                        // Independent Contractor
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Independent Contractor.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("The")
                                SpanText(" Parties ", BoldItalic.toModifier())
                                Text("intend to create an independent contractor relationship and nothing in this")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("shall operate or be construed as making")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("and")
                                SpanText(" Customer ", BoldItalic.toModifier())
                                Text("partners, joint ventures, principals, joint employers, agents, or employees of or with the other.")

                            }
                        }

                        // Compensation
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Compensation.", BoldUnderline.toModifier().margin(right = 8.px))
                                SpanText("Company ", BoldItalic.toModifier())
                                Text("will withold a portion of each rebate or incentive check tendered on behalf of")
                                SpanText(" Customer ", BoldItalic.toModifier())
                                Text("as sufficient compensation and consideration based on the Fee Table in Exhibit A, of the total rebate value tendered (\"")
                                SpanText("Service Fee", BoldItalic.toModifier())
                                Text("\"). In the event")
                                SpanText(" Customer", BoldItalic.toModifier())
                                Text(", or another third-party acting on ")
                                SpanText(" Customer's ", BoldItalic.toModifier())
                                Text("behalf, procure a rebate or incentive check without ")
                                SpanText(" Company's ", BoldItalic.toModifier())
                                Text("written authorization, ")
                                SpanText(" Customer ", BoldItalic.toModifier())
                                Text("agrees to transfer and endorse, if applicable, any such rebates or incentives payment to  ")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("within fourteen days (14 days) of receipt of payment. Authorization by ")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("shall be granted, or withheld, in ")
                                SpanText(" Company's ", BoldItalic.toModifier())
                                Text("sole and absolute discretion. Failure of ")
                                SpanText(" Customer ", BoldItalic.toModifier())
                                Text("to transfer and endorse, if applicable, any such rebates or incentives payment to ")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("shall be a Breach of this")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text(" and may subject ")
                                SpanText(" Customer ", BoldItalic.toModifier())
                                Text(" to reasonable penalties and collection costs outlined in ")
                                SpanText(" Section 16, ", Modifier.fontStyle(FontStyle.Italic))
                                Text("provided such costs are not prohibited by applicable law or rebate program rules.")
                            }

                            Ol(attrs = t3TermsListAlphaStyle.toModifier().margin(left = 16.px).toAttrs()) {

                                // Time and Method of Payment
                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        SpanText(
                                            "Time and Method of Payment.",
                                            BoldUnderline.toModifier().margin(right = 8.px)
                                        )
                                        Text("All fees payable to")
                                        SpanText(" Company ", BoldItalic.toModifier())
                                        Text("under this")
                                        SpanText(" Agreement ", BoldItalic.toModifier())
                                        Text("shall be in accordance with the terms and conditions set for in this ")
                                        SpanText(" Section 3. ", Modifier.fontStyle(FontStyle.Italic))
                                        Text("All amounts payable under this ")
                                        SpanText(" Agreement ", BoldItalic.toModifier())
                                        Text("shall be payable in U.S Dollars (USD).")
                                        SpanText(" Company ", BoldItalic.toModifier())
                                        Text("shall remit to")
                                        SpanText(" Customer ", BoldItalic.toModifier())
                                        Text("the remainder of any rebate or incentive, minus applicable fess and expenses, within 60 days of the receipt by")
                                        SpanText(" Company ", BoldItalic.toModifier())
                                        Text("of any rebate or incentive payment.")
                                    }
                                }

                                // Verification
                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        SpanText("Verification.", BoldUnderline.toModifier().margin(right = 8.px))
                                        Text("Equipment installations may be selected for an on-site verification review by ")
                                        SpanText(" Company, ", BoldItalic.toModifier())
                                        Text("or ")
                                        SpanText(" Service Provider. ", BoldItalic.toModifier())
                                        Text("Should a ")
                                        SpanText(" Customer's ", BoldItalic.toModifier())
                                        Text("facility be chosen for verification, satisfactory completion of that verification must occur before payment will be issued by")
                                        SpanText(" Service Provider. Service Provider's ", BoldItalic.toModifier())
                                        Text("verification is for the purpose of incentive payment only. No warranty is implied by either ")
                                        SpanText(" Service Provider ", BoldItalic.toModifier())
                                        Text("or")
                                        SpanText(" Company.", BoldItalic.toModifier())

                                    }
                                }
                            }
                        }

                        // Term and Termination
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Term and Termination.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("This")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("shall remain in effect until the ")
                                SpanText(" Parties ", BoldItalic.toModifier())
                                Text(" no longer have any unfulfilled obligations under this ")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("(\"")
                                SpanText("Term", BoldItalic.toModifier())
                                Text("\").")
                            }

                            Ol(attrs = t3TermsListAlphaStyle.toModifier().margin(left = 16.px).toAttrs()) {

                                // Term of scope of Work.
                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        SpanText(
                                            "Term of scope of Work.",
                                            BoldUnderline.toModifier().margin(right = 8.px)
                                        )
                                        Text("The")
                                        SpanText(" Term ", BoldItalic.toModifier())
                                        Text("for this ")
                                        SpanText(" Agreement ", BoldItalic.toModifier())
                                        Text("shall remain in effect until the earlier to occur of: (i) termination of this")
                                        SpanText(" Agreement ", BoldItalic.toModifier())
                                        Text("by")
                                        SpanText(" Company ", BoldItalic.toModifier())
                                        Text("in accordance with this ")
                                        SpanText(" Section 4; ", Modifier.fontStyle(FontStyle.Italic))
                                        Text("or (ii) the tendering of the rebate check to the ")
                                        SpanText(" Customer ", BoldItalic.toModifier())
                                        Text("minus the amount withheld by the")
                                        SpanText(" Company ", BoldItalic.toModifier())
                                        Text("as outlined in ")
                                        SpanText(" Section 3 ", Modifier.fontStyle(FontStyle.Italic))
                                        Text("herein.")
                                    }
                                }

                                // Termination for Material Breach.
                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        SpanText(
                                            "Termination for Material Breach.",
                                            BoldUnderline.toModifier().margin(right = 8.px)
                                        )
                                        Text("In the event of a material breach or default,")
                                        SpanText(" Company ", BoldItalic.toModifier())
                                        Text("may terminate this ")
                                        SpanText(" Agreement ", BoldItalic.toModifier())
                                        Text("as set forth in this")
                                        SpanText(" Section 4 ", Modifier.fontStyle(FontStyle.Italic))
                                        Text("without limiting other rights and remedies as set forth in this")
                                        SpanText(" Agreement. Customer ", BoldItalic.toModifier())
                                        Text("hereby acknowledges that if")
                                        SpanText(" Customer ", BoldItalic.toModifier())
                                        Text("terminates this")
                                        SpanText(" Agreement ", BoldItalic.toModifier())
                                        Text("for any reason prior to expiration of the")
                                        SpanText(" Term, Customer ", BoldItalic.toModifier())
                                        Text("is responsible for any, and all, amounts owing derived from the procurement of services rendered, and any other applicable penalties and fees set forth in this")
                                        SpanText(" Agreement.", BoldItalic.toModifier())


                                    }
                                }

                                // Termination for Material Breach.
                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        SpanText(
                                            "Termination for Convenience.",
                                            BoldUnderline.toModifier().margin(right = 8.px)
                                        )
                                        Text("Upon the provision of five (5) business days prior written notice to")
                                        SpanText(" Customer, Company ", BoldItalic.toModifier())
                                        Text("shall have the right to terminate without cause this")
                                        SpanText(" Agreement. ", BoldItalic.toModifier())

                                    }
                                }
                            }
                        }

                        // Events of Default and Remedies
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText(
                                    "Events of Default and Remedies.",
                                    BoldUnderline.toModifier().margin(right = 8.px)
                                )
                                Text("In the event such a material breach or default is of such nature that it cannot be cured or remedied within thirty (30) days after receipt of notice to cure remedy from")
                                SpanText(" Company, ", BoldItalic.toModifier())
                                Text("then")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("may institute such proceedings as may be necessary or desirable in its sole discretion to cure and remedy such default or breach.")
                            }
                        }

                        // Assignment and Assumption
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Assignment and Assumption.", BoldUnderline.toModifier().margin(right = 8.px))
                                SpanText("Customer ", BoldItalic.toModifier())
                                Text("hereby designates")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("as the assigned payee of any rebates or incentives from the")
                                SpanText(" Service Provider ", BoldItalic.toModifier())
                                Text("or third-party acting on the ")
                                SpanText(" Service Provider's ", BoldItalic.toModifier())
                                Text("behalf in service to the rebate and incentive program.")
                                SpanText(" Customer ", BoldItalic.toModifier())
                                Text("agrees to assign all right, title, interest, and benefits of the rebates and incentives and any final monies received therefrom to")
                                SpanText(" Company. ", BoldItalic.toModifier())
                            }

                            Ol(attrs = t3TermsListAlphaStyle.toModifier().margin(left = 16.px).toAttrs()) {

                                // Assumption
                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        SpanText("Assumption. ", BoldUnderline.toModifier().margin(right = 8.px))
                                        SpanText("Company ", BoldItalic.toModifier())
                                        Text("agrees to assume all of the")
                                        SpanText(" Customer's ", BoldItalic.toModifier())
                                        Text("interests, rights, duties, and obligations in the rebates and incentives therewith upon installation of the equipment.")
                                    }
                                }

                                // Responsibility
                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        SpanText("Responsibility. ", BoldUnderline.toModifier().margin(right = 8.px))
                                        Text("If the rebates or incentives are rejected or partially unpaid due to actions or delays caused by the")
                                        SpanText(" Customer, ", BoldItalic.toModifier())
                                        Text("or")
                                        SpanText(" Service Provider, ", BoldItalic.toModifier())
                                        Text("the")
                                        SpanText(" Customer ", BoldItalic.toModifier())
                                        Text("remains responsible and obligated to pay up to a specified amount for the")
                                        SpanText(" Company's Service Fee.", BoldItalic.toModifier())
                                    }
                                }

                                // Transfer of Payments
                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        SpanText(
                                            "Transfer of Payments. ",
                                            BoldUnderline.toModifier().margin(right = 8.px)
                                        )
                                        Text("If at any time")
                                        SpanText(" Customer ", BoldItalic.toModifier())
                                        Text("comes into possession of any payments related to rebates or incentives in relation to qualifying")
                                        SpanText(" Measures, ", BoldItalic.toModifier())
                                        Text("as outlined in the attached Exhibit B, then")
                                        SpanText(" Customer ", BoldItalic.toModifier())
                                        Text("agrees to transfer and endorse, if applicable, any such rebates or incentives payment to")
                                        SpanText(" Company ", BoldItalic.toModifier())
                                        Text("within fourteen days (14 Days) of receipt of payment.")
                                    }
                                }

                                // Transfer of Payments
                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        SpanText("Documentation.", BoldUnderline.toModifier().margin(right = 8.px))
                                        SpanText("Customer ", BoldItalic.toModifier())
                                        Text("shall provide all necessary documents and signatures to facilitate the assignment.")
                                    }
                                }
                            }
                        }

                        // Events of Default and Remedies
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText(
                                    "Representations and Warranties",
                                    BoldUnderline.toModifier().margin(right = 8.px)
                                )
                                Text("The")
                                SpanText(" Parties ", BoldItalic.toModifier())
                                Text("represent and warrant that it has the legal power to enter into this")
                                SpanText(" Agreement. ", BoldItalic.toModifier())
                            }
                        }
                    }

                }

                Spacer()

                Column(
                    modifier = Modifier.fillMaxWidth().margin(bottom = 54.px).pageStyleLeftRightPadding(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    t3DocumentFooter(currPage = 2, maxPages = 3)
                }

            }

            // Page 3
            Div(pageStyle.toModifier().toAttrs()) {

                Column(
                    modifier = Modifier.margin(top = 54.px).pageStyleLeftRightPadding().fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    t3DocumentHeader()
                }

                Column(
                    modifier = Modifier.margin(top = 16.px).padding(leftRight = 24.px).fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {

                    // List of paragraphs - Continuing off list item 8.
                    Ol(t3TermsListStyle.toModifier().margin(top = 8.px).toAttrs {
                        style {
                            property("counter-reset", "item 7")
                        }
                    }) {

                        // Notices
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Notices.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("Any notice or demand desired or required to be given hereunder shall be in writing and given by personal delivery, certified or registered mail, or facsimile addressed as follows:")
                            }
                        }

                        // To Company ; To Customer
                        Row(
                            Modifier.fillMaxWidth().margin(topBottom = 16.px),
                            horizontalArrangement = Arrangement.SpaceEvenly
                        ) {

                            // To Company
                            Column(Modifier, horizontalAlignment = Alignment.Start) {
                                P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                    Text("To")
                                    SpanText(" Company", BoldItalic.toModifier())
                                }

                                P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                    Text("Green Rebates, L.L.C.")
                                }

                                P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                    Text(RES.GENERAL.STREET_ADDRESS)
                                }

                                P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                    Text(RES.GENERAL.CITY_STATE_ZIP_ADDRESS)
                                }
                            }

                            // To Customer
                            Column(Modifier, horizontalAlignment = Alignment.Start) {
                                P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                    Text("To")
                                    SpanText(" Customer", BoldItalic.toModifier())
                                }

                                P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                    Text(t3CompanyName)
                                }

                                P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                    Text(t3CompanyAddress)
                                }

                                P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                    Text("$t3City, $t3State, $t3Zip")
                                }
                            }

                        }

                        // Binding
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Binding.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("The rights of ")
                                SpanText(" Customer ", BoldItalic.toModifier())
                                Text("hereunder shall not be assigned or transferred without")
                                SpanText(" Company's ", BoldItalic.toModifier())
                                Text("prior written consent.")
                            }
                        }

                        // Entire Agreement
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Entire Agreement.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("This")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("contains the entire agreement between the")
                                SpanText(" Parties ", BoldItalic.toModifier())
                                Text("hereto; no representations, inducements, promises or agreements, oral or other, between the")
                                SpanText(" Parties ", BoldItalic.toModifier())
                                Text("not embodied herein, shall be of any force and effect.")
                            }
                        }

                        // Amendment of Agreement
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Amendment of Agreement.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("This")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("may be amended only by a written instrument signed by ")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("in its sole discretion.")
                            }
                        }

                        // Amendment of Agreement
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Exhibits.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("If there are any terms and conditions contained in any exhibit attached hereto which are inconsistent with or additional to the terms and conditions contained in this")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("the terms and conditions of this ")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("shall prevail over any inconsistent terms, and unless specifically accepted by the")
                                SpanText(" Parties ", BoldItalic.toModifier())
                                Text("as part of this")
                                SpanText(" Agreement", BoldItalic.toModifier())
                                Text(", the additional terms shall not be incorporated into the")
                                SpanText(" Agreement.", BoldItalic.toModifier())
                            }
                        }

                        // Amendment of Agreement
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText(
                                    "Estimated Rebate Disclaimer.",
                                    BoldUnderline.toModifier().margin(right = 8.px)
                                )
                                Text("The figures provided in the")
                                SpanText(" 'Rebate Estimate Summary' ", Modifier.fontStyle(FontStyle.Italic))
                                Text("of this")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("are based on current available data and provided to")
                                SpanText(" Customer ", BoldItalic.toModifier())
                                Text("for informational purposes only. Final rebate or incentive values are determined solely by the applicable")
                                SpanText(" Service Provider ", BoldItalic.toModifier())
                                Text("or Program administrator and may vary.")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("does not guarantee the accuracy or payment of any estimated rebate amounts.")
                            }
                        }

                        // Governing Law
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Governing Law.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("The")
                                SpanText(" Parties ", BoldItalic.toModifier())
                                Text("agree that this")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("will be governed in accordance with the laws of the State of Washington, without regard to its principles of conflict of laws. The ")
                                SpanText(" Parties ", BoldItalic.toModifier())
                                Text("stipulate that any action brought pursuant to, or arising from, this")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("must be filed in King County, Washington, or, for matters involving federal jurisdiction, in the United States District Court for the Western District of Washington.")
                            }
                        }

                        // Taxes
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Taxes.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("The amounts to be withheld by ")
                                SpanText(" Company ", BoldItalic.toModifier())
                                Text("does not include any taxes.")
                            }
                        }

                        // Penalties and Fees
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Penalties and Fees.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("In the event of a material breach, or default, of this ")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("by ")
                                SpanText(" Customer", BoldItalic.toModifier())
                                Text(", the following penalties and fees may apply in addition to collection of compensation as outlined in")
                                SpanText(" Section 3", Modifier.fontStyle(FontStyle.Italic))
                                Text(", at the sole discretion of")
                                SpanText(" Company: ", BoldItalic.toModifier())
                            }

                            Ol(attrs = t3TermsListAlphaStyle.toModifier().margin(left = 16.px).toAttrs()) {

                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        Text("Penalties in an amount up to ten percent (10%) of the total rebate or incentive payment;")
                                    }
                                }

                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        Text("Administrative fees associated with any additional processing, handling, or enforcement efforts;")
                                    }
                                }

                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        Text("Interest charges on any unpaid amounts;")
                                    }
                                }

                                Li(t3TermsListItemStyle.toAttrs()) {
                                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                        Text("Attorneys' fees as outlined in")
                                        SpanText(" Section 17;", Modifier.fontStyle(FontStyle.Italic))
                                    }
                                }
                            }

                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                Text("These remedies are in addition to, and not in limitation of, any other rights or remedies available under this")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("or at law.")
                            }
                        }

                        // Attorney's Fees
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Attorney's Fees.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("In the event any legal action is taken by either")
                                SpanText(" Party ", BoldItalic.toModifier())
                                Text("against the other")
                                SpanText(" Party ", BoldItalic.toModifier())
                                Text("to enforce any of the terms and conditions of this")
                                SpanText(" Agreement", BoldItalic.toModifier())
                                Text(", it is agreed that the unsuccessful party to such action shall pay to the prevailing party therein all court costs, reasonable attorney's fees and expenses incurred by the prevailing party.")
                            }
                        }

                        // Counterparts
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Counterparts.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("This")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("may be executed in duplicate originals, each of which shall be deemed to be an original instrument. All such counterparts and duplicate originals together shall constitute but one")
                                SpanText(" Agreement", BoldItalic.toModifier())
                                Text(".")
                            }
                        }

                        // Severability
                        Li(t3TermsListItemStyle.toAttrs()) {
                            P(t3TightParagraphStyle.toModifier().toAttrs()) {
                                SpanText("Severability.", BoldUnderline.toModifier().margin(right = 8.px))
                                Text("If any provision of this")
                                SpanText(" Agreement ", BoldItalic.toModifier())
                                Text("is held to be invalid, illegal, or unenforceable, under applicable law, such provision shall be modified to the minimum extent necessary to make it valid and enforceable while preserving the")
                                SpanText(" Parties' ", BoldItalic.toModifier())
                                Text("original intent. If such modification is not possible, the offending provision shall be deemed severed from this")
                                SpanText(" Agreement, ", BoldItalic.toModifier())
                                Text("and the remaining provisions shall remain in full force and effect.")
                            }
                        }

                    }
                }

                Column(
                    modifier = Modifier.fillMaxWidth().margin(topBottom = 32.px).pageStyleLeftRightPadding()
                        .fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    // IN WITNESS WHEREOF...
                    P(t3TightParagraphStyle.toModifier().toAttrs()) {
                        Text("IN WITNESS WHEREOF, the")
                        SpanText(" Parties ", BoldItalic.toModifier())
                        Text("hereto have executed this ")
                        SpanText(" Agreement ", BoldItalic.toModifier())
                        Text("as of the")
                        SpanText(" Effective Date.", BoldItalic.toModifier())

                    }

                    // To Company ; To Customer
                    Row(
                        Modifier.fillMaxWidth().margin(topBottom = 32.px).pageStyleLeftRightPadding(),
                        horizontalArrangement = Arrangement.SpaceEvenly
                    ) {

                        // On behalf of, Customer
                        Column(Modifier.width(300.px), horizontalAlignment = Alignment.Start) {
                            P(GeneralParagraphStyle.toModifier().toAttrs()) {
                                Text("On behalf of, ")
                                SpanText("Customer", BoldItalic.toModifier())
                            }

                            t3SignatureRow("By:")

                            t3SignatureRow("Name:")

                            t3SignatureRow("Title:")
                        }

                        // On behalf of, Company
                        Column(Modifier.width(300.px), horizontalAlignment = Alignment.Start) {
                            P(GeneralParagraphStyle.toModifier().toAttrs()) {
                                Text("On behalf of, ")
                                SpanText("Company", BoldItalic.toModifier())
                            }

                            t3SignatureRow("By:")

                            t3SignatureRow("Name:", repGreenRebates)

                            t3SignatureRow("Title:", repTitle)
                        }
                    }
                }

                Spacer()

                Column(
                    modifier = Modifier.fillMaxWidth().margin(bottom = 54.px).pageStyleLeftRightPadding(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    t3DocumentFooter(currPage = 3, maxPages = 3)
                }

            }

        }
    }
}
